import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Header, Icon, Input, Label, Loader, Message, Modal } from "semantic-ui-react";
import { createColumnHelper } from "@tanstack/react-table";
import { bookingApi } from "../api/api";
import DataTable from "../components/admin/DataTable";
import { dayStr, fmtDateAndTime, roomLabel } from "../components/admin/adminHelpers";
import { toDevanagari } from "../utils/devanagari";

/* The night desk's whole screen: today's and tomorrow's movements, one table
   per guest house, rooms in order.

   Arrivals and departures are deliberately not split apart. The desk works the
   building room by room, and on a turnover day the guest leaving 214 and the
   guest arriving into it are the same piece of work — two tables would put
   them on opposite ends of the screen. What each row *is* is a column instead.

   Deliberately outside BookingContext. That context loads and polls on
   `role === 'admin'` and fans out to endpoints a night token is refused
   (bookings list, heartbeat, dashboard), so a night session would sit on an
   empty store forever. One endpoint, one poll, owned here. */

/* Long enough that a desk terminal isn't hammering the server all night, short
   enough that two people working the same booking find out quickly. */
const REFRESH_MS = 30000;

const ARRIVAL = "ARRIVAL";

/* Which script the two name columns are written in. Only the names: room
   numbers, dates, the department and every label on the screen stay as they
   are, because what the toggle is for is reading a guest's name off the sheet,
   not translating the desk's own vocabulary. Remembered per terminal — a night
   clerk who reads Hindi shouldn't have to set it again after every refresh. */
const LANG_KEY = "nightdesk.lang";

const storedLang = () => {
  try {
    return localStorage.getItem(LANG_KEY) === "hi" ? "hi" : "en";
  } catch {
    /* Private windows and locked-down desk browsers throw on access rather
       than returning null. English is the safe default either way. */
    return "en";
  }
};

const rememberLang = (lang) => {
  try {
    localStorage.setItem(LANG_KEY, lang);
  } catch {
    /* Nothing to do — the toggle still works for this session. */
  }
};

/* A room held for turnover can't take a check-in — the server refuses it with
   ROOM_IN_MAINTENANCE, so say so before the round trip rather than after. */
const MAINTENANCE = "MAINTENANCE";

/* The full enum values are too wide for a column read at a glance; the printed
   occupancy sheet abbreviates them the same way. */
const BOOKING_TYPE_LABEL = { OFFICIAL: "Official", NON_OFFICIAL: "Non-Official" };

const columnHelper = createColumnHelper();

/* What the button on this row can do, and if it can't, why not. The server is
   still the authority — every one of these guards exists there too — but a
   disabled button with a reason beats a red banner after the fact. */
function actionFor(m) {
  if (m.kind === ARRIVAL) {
    if (m.actual_checkin) return { done: `Checked in ${clockOf(m.actual_checkin)}` };
    if (m.date > dayStr(0)) return { blocked: "Opens on the arrival date" };
    if (m.room_status === MAINTENANCE) return { blocked: "Room under maintenance" };
    return { verb: "checkin", label: "Check In", colour: "green" };
  }

  if (m.actual_checkout) return { done: `Checked out ${clockOf(m.actual_checkout)}` };
  if (!m.actual_checkin) return { blocked: "Not checked in" };
  return { verb: "checkout", label: "Check Out", colour: "blue" };
}

/* Just the clock time of a timestamp. fmtDateTime would print the date too,
   which on a sheet covering only today and tomorrow is a column's worth of
   width spent restating what "When" already says. */
const clockOf = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
};

const rowKey = (m) => `${m.kind}-${m.assignment_id}`;

/* Room numbers are text but read as numbers: "9" comes before "10", and "LD-2"
   before "LD-10". `numeric` does that without parsing the shape of a room
   number, which varies by guest house (101, C.Hall, LD-2). */
const byRoomThenTime = (a, b) =>
  a.room_number.localeCompare(b.room_number, undefined, { numeric: true, sensitivity: "base" })
  || (a.date < b.date ? -1 : a.date > b.date ? 1 : 0)
  || a.time.localeCompare(b.time);

/* A guest past their departure who never checked out. No badge — the desk asked
   for the labels gone — but the row still carries a tint, or an overstay would
   be indistinguishable from an ordinary departure in a table sorted by room. */
const rowStyle = (m) => (m.overdue ? { backgroundColor: "#fff1f0" } : undefined);

/* The two name columns, in whichever script is switched on. Transliteration is
   phonetic and can only ever approximate a name, so the English spelling stays
   one hover away rather than being replaced outright. */
const inScript = (name, lang) =>
  lang === "hi" && name ? toDevanagari(name) : name;

const nameTitle = (name, lang) =>
  lang === "hi" && name ? name : undefined;

/* What the search box looks at. Deliberately not every field on the row: the
   desk searches for a person or a room they have in front of them, and folding
   in department or booking type would make "official" match half the sheet. */
const haystack = (m) =>
  [m.room_number, m.guest_name, m.applicant_name, m.guest_mobile]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

/* Substring matching, on each whitespace-separated term independently, so
   "anil 205" narrows to that guest in that room rather than looking for the
   literal phrase. Terms may match different fields, and a term can land mid-
   value — "9821" finds the mobile, "kumar" the surname. Pure, so the matching
   rules can be tested without a DOM. */
export function matchesQuery(m, query) {
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return true;
  const hay = haystack(m);
  return terms.every((t) => hay.includes(t));
}

export default function NightDeskPage() {
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [syncedAt, setSyncedAt] = useState(null);
  const [confirming, setConfirming] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [query, setQuery] = useState("");
  const [lang, setLang] = useState(storedLang);

  /* The refresh interval must not restart every time `movements` changes, so
     the poll reads the loader through a ref rather than closing over state. */
  const loadRef = useRef(null);

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const data = await bookingApi.movements();
      setMovements(data.movements || []);
      setError(null);
      setSyncedAt(new Date());
    } catch (e) {
      setError(e.message || "Could not load tonight's list.");
    } finally {
      setLoading(false);
    }
  }, []);
  loadRef.current = load;

  useEffect(() => { load(); }, [load]);

  /* Poll while the screen is actually being looked at. A desk terminal is left
     open all night, and there is nothing to refresh for while it's minimised. */
  useEffect(() => {
    let timer = null;
    const tick = () => loadRef.current({ quiet: true });
    const start = () => { if (!timer) timer = setInterval(tick, REFRESH_MS); };
    const stop = () => { clearInterval(timer); timer = null; };
    const onVisibility = () => {
      if (document.hidden) stop();
      else { tick(); start(); }
    };
    document.addEventListener("visibilitychange", onVisibility);
    if (!document.hidden) start();
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  const runAction = async () => {
    const { movement: m, action } = confirming;
    setConfirming(null);
    setBusyId(rowKey(m));
    setError(null);
    try {
      /* By assignment, not room: one booking can hold the same room twice, so
         a room id no longer names a single stay. The movements payload carries
         `assignment_id` for exactly this. */
      if (action.verb === "checkin") await bookingApi.checkin(m.booking_id, [m.assignment_id]);
      else await bookingApi.checkout(m.booking_id, [m.assignment_id]);
      setNotice(
        `${m.guest_name || "Guest"} · Room ${roomLabel(m.room_number, m.guest_house_short_name)} — ` +
        `${action.verb === "checkin" ? "checked in" : "checked out"}.`,
      );
      await load({ quiet: true });
    } catch (e) {
      setError(e.message || "That didn't go through.");
      /* Someone else got there first, the room's state moved under us, or it
         turned out to still be held by another live booking — either way
         what's on screen is stale, so replace it. */
      if (e.code === "ALREADY_DONE" || e.code === "ROOM_IN_MAINTENANCE" || e.code === "ROOM_OCCUPIED") {
        await load({ quiet: true });
      }
    } finally {
      setBusyId(null);
    }
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor("room_number", {
        header: "Room",
        cell: (info) => (
          <strong>{roomLabel(info.getValue(), info.row.original.guest_house_short_name)}</strong>
        ),
      }),
      columnHelper.accessor("room_type", {
        header: "Room Type",
        cell: (info) => info.getValue() || "—",
      }),
      /* Whoever is named on this specific room. The server already resolves
         `assignment.guest_name or booking.guest_name`, so a multi-room booking
         shows the person actually holding each key rather than the booking's
         headline guest repeated down the table. */
      columnHelper.accessor("guest_name", {
        header: "Guest",
        cell: (info) => {
          const m = info.row.original;
          return (
            <span style={{ fontWeight: 600 }} title={nameTitle(m.guest_name, lang)}>
              {inScript(m.guest_name, lang) || "—"}
              {m.room_status === MAINTENANCE && (
                <Label color="orange" size="mini" style={{ marginLeft: 6 }}>Maintenance</Label>
              )}
            </span>
          );
        },
      }),
      columnHelper.accessor("pax", {
        header: "Pax",
        cell: (info) => info.getValue() ?? "—",
      }),
      columnHelper.accessor("department", {
        header: "Department",
        cell: (info) => info.getValue() || "—",
      }),
      columnHelper.accessor("guest_mobile", {
        header: "Mobile",
        cell: (info) => {
          const number = info.getValue();
          return number ? <a href={`tel:${number}`}>{number}</a> : "—";
        },
      }),
      columnHelper.accessor("booking_number", {
        header: "Booking ID",
        cell: (info) => info.getValue() || "—",
      }),
      columnHelper.accessor("booking_type", {
        header: "Booking Type",
        cell: (info) => BOOKING_TYPE_LABEL[info.getValue()] || info.getValue() || "—",
      }),
      columnHelper.accessor("applicant_name", {
        header: "Applicant",
        cell: (info) => (
          <span title={nameTitle(info.getValue(), lang)}>
            {inScript(info.getValue(), lang) || "—"}
          </span>
        ),
      }),
      columnHelper.accessor("date", {
        header: "When",
        cell: (info) => {
          const m = info.row.original;
          /* A booking with no time recorded shows the guest house default
             rather than a blank. Muted, with the reason on hover, so it can't
             be mistaken for a time the guest actually gave. */
          return (
            <span
              style={{ fontWeight: m.time_is_default ? 400 : 600, opacity: m.time_is_default ? 0.6 : 1 }}
              title={m.time_is_default ? "No time recorded — guest house default" : undefined}
            >
              {fmtDateAndTime(m.date, `${m.time}:00`)}
            </span>
          );
        },
      }),
      columnHelper.display({
        id: "action",
        header: "Action",
        enableSorting: false,
        cell: (info) => {
          const m = info.row.original;
          const action = actionFor(m);
          if (action.done) {
            return (
              <span style={{ color: "#21ba45", fontWeight: 600, whiteSpace: "nowrap", fontSize: ".9em" }}>
                <Icon name="check circle" />{action.done}
              </span>
            );
          }
          if (action.blocked) {
            return (
              <span style={{ color: "rgba(0,0,0,.4)", fontSize: ".9em", whiteSpace: "nowrap" }}>
                {action.blocked}
              </span>
            );
          }
          return (
            <Button
              /* mini, not small: the row button is what sets the row's height,
                 and a taller one undoes the single-line table. */
              size="mini"
              color={action.colour}
              loading={busyId === rowKey(m)}
              disabled={!!busyId}
              onClick={() => setConfirming({ movement: m, action })}
              content={action.label}
            />
          );
        },
      }),
    ],
    [busyId, lang],
  );

  /* The search narrows the sheet itself, before it is grouped: the per-house
     counts and the "to do" badge then describe what is actually on screen
     rather than what would be there unfiltered. A guest house left with no
     matches drops out entirely instead of rendering an empty table. */
  const visible = useMemo(
    () => movements.filter((m) => matchesQuery(m, query)),
    [movements, query],
  );

  /* One group per guest house, alphabetical, rooms in order within it. Built
     from the movements themselves, so a guest house with nothing checking in or
     out simply has no group and never renders an empty table.

     Days are resolved from the payload's own dates rather than a label held in
     state — a screen left open across midnight re-labels its rows on the next
     refresh instead of quietly calling yesterday "Today". */
  const houses = useMemo(() => {
    const byHouse = new Map();
    for (const m of visible) {
      const name = m.guest_house_name || "Unassigned";
      if (!byHouse.has(name)) byHouse.set(name, []);
      byHouse.get(name).push(m);
    }
    return [...byHouse.entries()]
      .filter(([, rows]) => rows.length > 0)
      .map(([name, rows]) => ({
        name,
        rows: [...rows].sort(byRoomThenTime),
        /* For the header: what is still outstanding is the number worth seeing
           at a glance, not how many rows the table has. */
        pending: rows.filter((m) => actionFor(m).verb).length,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [visible]);


  return (
    <div style={{ padding: "4px 0 60px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18, flexWrap: "wrap" }}>
        {/* Room, guest, applicant, mobile — the four things the desk has to go
            on when someone is standing at it. Not debounced: the whole sheet is
            already in memory, so filtering it is a keystroke's worth of work. */}
        <Input
          icon="search"
          iconPosition="left"
          placeholder="Search room, guest, applicant or mobile…"
          value={query}
          onChange={(e, { value }) => setQuery(value)}
          /* The clear button is an action rather than a second icon: Semantic
             allows one icon per input, and swapping the search glyph for a
             cross would shift the text under the cursor as you type. */
          action={
            query
              ? {
                  icon: "close",
                  basic: true,
                  "aria-label": "Clear search",
                  onClick: () => setQuery(""),
                }
              : undefined
          }
          style={{ minWidth: 340 }}
        />
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>
          {syncedAt && (
            <span style={{ color: "rgba(0,0,0,.45)", fontSize: ".85rem" }}>
              Refreshed {syncedAt.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
            </span>
          )}
          {/* Two buttons rather than a checkbox: on a shared terminal the
              state has to be readable across the desk, and which one is lit
              says it without anyone having to parse a label. */}
          <Button.Group basic size="small">
            <Button
              active={lang === "en"}
              onClick={() => { setLang("en"); rememberLang("en"); }}
              content="English"
            />
            <Button
              active={lang === "hi"}
              onClick={() => { setLang("hi"); rememberLang("hi"); }}
              content="हिंदी"
            />
          </Button.Group>
          <Button
            basic icon="refresh" content="Refresh"
            loading={loading} disabled={loading}
            onClick={() => load()}
          />
        </div>
      </div>

      {error && <Message negative onDismiss={() => setError(null)} content={error} />}
      {notice && <Message positive onDismiss={() => setNotice(null)} content={notice} />}

      {loading && movements.length === 0 ? (
        <Loader active inline="centered" content="Loading tonight's list…" />
      ) : houses.length === 0 ? (
        <Message
          info
          content={
            query
              ? `Nothing on tonight's list matches “${query}”.`
              : "Nothing is arriving or departing today or tomorrow."
          }
        />
      ) : (
        houses.map((house) => (
          <div key={house.name} style={{ marginBottom: 34 }}>
            <Header
              as="h3"
              style={{ borderBottom: "2px solid #dcddde", paddingBottom: 8, marginBottom: 14 }}
            >
              {house.name}
              <Label circular style={{ marginLeft: 10 }}>{house.rows.length}</Label>
              {house.pending > 0 && (
                <Label color="yellow" size="small" style={{ marginLeft: 6 }}>
                  {house.pending} to do
                </Label>
              )}
            </Header>
            <DataTable
              data={house.rows}
              columns={columns}
              batchSize={50}
              minWidth={1400}
              getRowStyle={rowStyle}
              /* Nine columns of guest detail, read across a desk at arm's
                 length — the 1800px cap on .bhu-main leaves the far columns
                 cramped on a wide monitor for no gain. Same opt-out SummaryTab
                 takes, held just off the window's edges so the list reads as a
                 panel rather than as the whole screen. */
              fullBleed
              bleedWidth={0.9}
            />
          </div>
        ))
      )}

      {/* One tap on a shared terminal at 3am should not be able to check the
          wrong guest out of the wrong room. */}
      <Modal open={!!confirming} size="tiny" onClose={() => setConfirming(null)}>
        <Modal.Header>{confirming?.action.label}</Modal.Header>
        <Modal.Content>
          <p style={{ fontSize: "1.1rem" }}>
            {confirming?.action.verb === "checkin" ? "Check in " : "Check out "}
            <strong>{confirming?.movement.guest_name || "this guest"}</strong>
            {confirming?.action.verb === "checkin" ? " to " : " of "}
            <strong>
              Room {confirming && roomLabel(confirming.movement.room_number, confirming.movement.guest_house_short_name)}
            </strong>?
          </p>
          <p style={{ color: "rgba(0,0,0,.5)" }}>
            {confirming?.movement.booking_number} · {confirming?.movement.guest_house_name}
            {confirming?.movement.guest_mobile ? ` · ${confirming.movement.guest_mobile}` : ""}
          </p>
        </Modal.Content>
        <Modal.Actions>
          <Button content="Cancel" onClick={() => setConfirming(null)} />
          <Button
            color={confirming?.action.colour}
            content={confirming?.action.label}
            onClick={runAction}
          />
        </Modal.Actions>
      </Modal>
    </div>
  );
}
